import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Empty } from "@/components/Empty";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { AddConversionMappingModal } from "@/pages/applications/pages/googleAdsIntegration/conversionMappings/AddConversionMappingModal";
import { useConversionActions } from "@/pages/applications/pages/googleAdsIntegration/conversionMappings/api/useConversionActions";
import {
  type ConversionMapping,
  useDeleteConversionMapping,
} from "@/pages/applications/pages/googleAdsIntegration/conversionMappings/api/useConversionMappings";

const cssCard = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: ${theme.colors.staticBgContainer};
  border-radius: 8px;
`;

const cssCardHeader = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const cssCardActions = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const cssBlock = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const cssEmptyContainer = css`
  padding: 48px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const cssEmpty = css`
  width: 80px;
  height: 64px;
`;

const cssTabHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface ConversionMappingRowProps {
  orgUuid: string;
  conversionMapping: ConversionMapping;
  conversionActionName: string | null;
  onEdit: (conversionMapping: ConversionMapping) => void;
}

const ConversionMappingRow: React.FC<ConversionMappingRowProps> =
  function ConversionMappingRow({
    orgUuid,
    conversionMapping,
    conversionActionName,
    onEdit,
  }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const deleteMutation = useDeleteConversionMapping(
      orgUuid,
      conversionMapping.googleAdsAccountId,
      conversionMapping.id,
      {
        onSuccess: () => {
          enqueueSnackbar(
            t(
              "applications.googleAds.conversions.disableSuccess",
              "Conversion disabled",
            ),
            { variant: "success" },
          );
        },
        onError: () => {
          enqueueSnackbar(
            t(
              "applications.googleAds.conversions.disableError",
              "Failed to disable conversion",
            ),
            { variant: "error" },
          );
        },
      },
    );

    const isActive = conversionMapping.status === "active";

    const summary = useMemo(() => {
      const parts: Array<string> = [];
      if (conversionActionName) {
        parts.push(
          t(
            "applications.googleAds.conversions.summary.action",
            "Conversion action: {{name}}",
            { name: conversionActionName },
          ),
        );
      }
      if (conversionMapping.conversionValue && conversionMapping.currencyCode) {
        parts.push(
          t(
            "applications.googleAds.conversions.summary.value",
            "Value: {{value}} {{currency}}",
            {
              value: conversionMapping.conversionValue,
              currency: conversionMapping.currencyCode,
            },
          ),
        );
      }
      return parts.join("; ");
    }, [conversionActionName, conversionMapping, t]);

    return (
      <div css={cssCard}>
        <div css={cssCardHeader}>
          {isActive ? (
            <Badge variant="positive">
              {t("applications.googleAds.conversions.status.active", "Active")}
            </Badge>
          ) : (
            <Badge variant="negative">
              {t(
                "applications.googleAds.conversions.status.disabled",
                "Disabled",
              )}
            </Badge>
          )}
        </div>
        {summary ? (
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {summary}
          </Typography>
        ) : null}
        <div css={cssCardActions}>
          <Button variant="secondary" onClick={() => onEdit(conversionMapping)}>
            {t("common.edit")}
          </Button>
          <Button
            variant="secondary"
            disabled={!isActive || deleteMutation.isPending}
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(undefined)}
          >
            {t("applications.googleAds.conversions.disable", "Disable")}
          </Button>
        </div>
      </div>
    );
  };

export const ConversionMappingsTab: React.FC =
  function ConversionMappingsTab() {
    const { t } = useTranslation();

    const managerAccountQuery = interlude.googleAds.useGetManagerAccount();
    const accountsQuery = interlude.googleAds.useListManagerAccountAccounts();

    const orgUuid = managerAccountQuery.data?.orgUuid ?? "";

    const conversionActionsQuery = useConversionActions(orgUuid);

    const accounts = useMemo(
      () => accountsQuery.data?.accounts ?? [],
      [accountsQuery.data],
    );

    const [editing, setEditing] = useState<ConversionMapping | null>(null);
    const [isAddOpen, setIsAddOpen] = useState(false);

    const handleAdd = useHandler(() => {
      setEditing(null);
      setIsAddOpen(true);
    });

    const handleEdit = useHandler((conversionMapping: ConversionMapping) => {
      setEditing(conversionMapping);
      setIsAddOpen(true);
    });

    const handleClose = useHandler(() => {
      setIsAddOpen(false);
      setEditing(null);
    });

    const handleSuccess = useHandler(() => {
      handleClose();
    });

    if (managerAccountQuery.isPending || accountsQuery.isPending) {
      return (
        <Stack gap="12px">
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      );
    }

    return (
      <Stack gap="16px">
        {accounts.length > 0 ? (
          <div css={cssTabHeader}>
            <div />
            <Button variant="primary" onClick={handleAdd}>
              {t("applications.googleAds.conversions.add", "+ Add Conversion")}
            </Button>
          </div>
        ) : null}

        {accounts.length === 0 ? (
          <div css={cssEmptyContainer}>
            <Empty css={cssEmpty} />
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t(
                "applications.googleAds.conversions.noAccounts",
                "Connect a Google Ads account first to configure conversions.",
              )}
            </Typography>
          </div>
        ) : (
          <Stack gap="12px">
            {accounts.map((acc) => {
              if (acc.id === null) return null;
              return (
                <AccountConversionMappingsBlock
                  key={acc.id}
                  orgUuid={orgUuid}
                  accountId={acc.id}
                  accountLabel={`${acc.googleAdsCustomerId} — ${acc.name}`}
                  conversionActionLookup={
                    conversionActionsQuery.data?.accounts.find(
                      (a) => a.id === acc.googleAdsCustomerId,
                    )?.conversionActions ?? []
                  }
                  onEdit={handleEdit}
                />
              );
            })}
          </Stack>
        )}

        {isAddOpen && orgUuid !== "" ? (
          <AddConversionMappingModal
            open={isAddOpen}
            orgUuid={orgUuid}
            editing={editing ?? undefined}
            onClose={handleClose}
            onSuccess={handleSuccess}
          />
        ) : null}
      </Stack>
    );
  };

interface AccountConversionMappingsBlockProps {
  orgUuid: string;
  accountId: number;
  accountLabel: string;
  conversionActionLookup: Array<{ conversionActionId: string; name: string }>;
  onEdit: (conversionMapping: ConversionMapping) => void;
}

const AccountConversionMappingsBlock: React.FC<AccountConversionMappingsBlockProps> =
  function AccountConversionMappingsBlock({
    orgUuid,
    accountId,
    accountLabel,
    conversionActionLookup,
    onEdit,
  }) {
    const { t } = useTranslation();
    const query = interlude.googleAds.useListConversionMappings({
      params: {
        orgUuid,
        googleAdsAccountId: accountId,
      },
    });

    const conversionMappings = query.data?.conversionMappings ?? [];

    return (
      <div css={cssBlock}>
        <Typography
          variant="body"
          fontWeight={500}
          color={theme.colors.staticFgTitle}
        >
          {accountLabel}
        </Typography>
        {query.isPending ? (
          <Skeleton variant="rounded" height={80} />
        ) : conversionMappings.length === 0 ? (
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t(
              "applications.googleAds.conversions.empty",
              "No conversions configured",
            )}
          </Typography>
        ) : (
          <Stack gap="12px">
            {conversionMappings.map((c) => {
              const actionName =
                conversionActionLookup.find(
                  (a) => a.conversionActionId === c.conversionActionId,
                )?.name ?? null;
              return (
                <ConversionMappingRow
                  key={c.id}
                  orgUuid={orgUuid}
                  conversionMapping={c}
                  conversionActionName={actionName}
                  onEdit={onEdit}
                />
              );
            })}
          </Stack>
        )}
      </div>
    );
  };
